// zustand/store/useCardData.ts
import { useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import { useCardStore } from "./UseCardStore";
import { CardFormValues } from "@/types/Card";
import { useBookmarkCard, useGetAllCard } from "../api/card.api";
import {
  useGetBookmarkedCards,
  useGetAddedToDeckCards,
} from "../api/booking.api";

export const useCardData = () => {
  const {
    setCards,
    setBookmarked,
    setLoading,
    setHasFetched,
    hasFetched,
    bookmarkedIds,
    setMyDeck,
  } = useCardStore();

  const { update: bookmarkCard } = useBookmarkCard({ key: ["bookmark-card"] });
  const { update: addToDeckCard } = useBookmarkCard({ key: ["add-to-deck"] });

  const { refetch: refetchAll } = useGetAllCard({ key: ["taas-cards"] });
  const { refetch: refetchBookmarks } = useGetBookmarkedCards({
    key: ["bookmark-card"],
  });

  const { refetch: refetchAddToDeck } = useGetAddedToDeckCards({
    key: ["add-to-deck"],
  });

  useEffect(() => {
    if (!hasFetched) {
      fetchData();
    }
  }, [hasFetched]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cardsRes, bookmarksRes, myDeckRes] = await Promise.all([
        refetchAll(),
        refetchBookmarks(),
        refetchAddToDeck(),
      ]);
      const cards = cardsRes.data?.data || [];
      const bookmarks = (bookmarksRes.data?.data || []).map(
        (c: CardFormValues) => c.id
      );

      const deck = (myDeckRes.data?.data || []).map(
        (c: CardFormValues) => c.id
      );
      setCards(cards);
      setBookmarked(bookmarks);
      setMyDeck(deck);
      setHasFetched(true);
    } catch (err: any) {
      enqueueSnackbar("Failed to load card data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleBookmark = (id?: string) => {
    const numericId = id ? parseInt(id) : undefined;
    if (!numericId) return;

    bookmarkCard(
      { card_id: numericId },
      {
        onSuccess: async (res) => {
          enqueueSnackbar(res.message || "Bookmarked", { variant: "success" });
          const updated = await refetchBookmarks();
          const updatedIds = (updated.data?.data || []).map(
            (c: CardFormValues) => c.id
          );
          setBookmarked(updatedIds);
        },
        onError: (err: any) => {
          enqueueSnackbar(err?.response?.data?.message || "Bookmark failed", {
            variant: "error",
          });
        },
      }
    );
  };

  const handleAddToDeck = (id?: string) => {
    const numericId = id ? parseInt(id) : undefined;
    if (!numericId) return;

    addToDeckCard(
      { card_id: numericId },
      {
        onSuccess: async (res) => {
          enqueueSnackbar(res.message || "Added to Deck", {
            variant: "success",
          });
          const updated = await refetchAddToDeck();
          const updatedIds = (updated.data?.data || []).map(
            (c: CardFormValues) => c.id
          );
          setBookmarked(updatedIds);
        },
        onError: (err: any) => {
          enqueueSnackbar(
            err?.response?.data?.message || "Adding card to deck failed",
            {
              variant: "error",
            }
          );
        },
      }
    );
  };

  return {
    ...useCardStore.getState(),
    handleBookmark,
    addToDeckCard,
  };
};
